import { useLocation, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import usePostStore from "../../store/post.store";
import { handleHttpError, useHandleNavigate } from "../../../utils/utils";
import { DialogContent, DialogHeader } from "./modal.core";
import StarIcon from "../../../assets/svgs/common/star.svg";
import { Form } from "../../../components/ui/form";
import Button from "./button.core";
import Textarea from "./textarea.core";
import { useRejectDraftHttp } from "../../hooks/http/posts/reject-draft.hook";
import { useRejectPostHttp } from "../../hooks/http/posts/reject-post.hook";
import { useDisqualifyPostHttp } from "../../hooks/http/posts/disqualify-posts.hook";
import { useFlagPostHttp } from "../../hooks/http/posts/flag-posts.hook";
import { useToast } from "../../../hooks/use-toast";

const FormSchema = z.object({
  message: z.string().min(5, "Please provide a reason for rejection"),
});

export const RejectPostModal: React.FunctionComponent = () => {
  const { postId } = useParams();
  const { toast } = useToast();

  const { fetching: fetchingRejectdraft, execute: rejectDraftHttp } =
    useRejectDraftHttp();

  const { fetching: fetchingRejectpost, execute: rejectPostHttp } =
    useRejectPostHttp();

  const { fetching: fetchingDisqualifypost, execute: disqualifyPostHttp } =
    useDisqualifyPostHttp();

  const { fetching: fetchingFlagpost, execute: flagPostHttp } =
    useFlagPostHttp();

  const location = useLocation();
  const { removeById } = usePostStore();

  function handleReturnPath() {
    if (location.pathname.endsWith("draft-reject")) {
      return location.pathname.replace(`/${postId}/draft-reject`, "");
    }
    if (location.pathname.endsWith("post-reject")) {
      return location.pathname.replace(`/${postId}/post-reject`, "");
    }
    if (location.pathname.endsWith("disqualify")) {
      return location.pathname.replace(`/${postId}/disqualify`, "");
    }
    if (location.pathname.endsWith("flag")) {
      return location.pathname.replace(`/${postId}/flag`, "");
    }
    // Fallback
    return location.pathname;
  }

  const { handleNavigate } = useHandleNavigate(handleReturnPath());

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      message: "",
    },
  });

  async function handleReject(data: z.infer<typeof FormSchema>) {
    if (!postId) return;
    if (location.pathname.endsWith("draft-reject")) {
      const response = await rejectDraftHttp({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response?.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
      return;
    }
    if (location.pathname.endsWith("post-reject")) {
      const response = await rejectPostHttp({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response?.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
      return;
    }
    if (location.pathname.endsWith("disqualify")) {
      const response = await disqualifyPostHttp({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response?.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
      return;
    }
    if (location.pathname.endsWith("flag")) {
      const response = await flagPostHttp({
        body: { message: data.message },
        pathParams: { id: postId },
      });
      if (!response?.success) {
        return handleHttpError(response, toast);
      }
      removeById(postId);
      handleNavigate();
      return;
    }
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    // if (data.message.length < 5) {
    //   toast({
    //     content: "Please provide proper a reason for rejection",
    //     toastType: "error",
    //   });
    //   return;
    // }

    await handleReject(data);
  }

  function ModalType() {
    if (location.pathname.endsWith("draft-reject")) {
      return {
        title: "Reason for rejecting draft",
        description: "Give a brief for the rejection of the draft",
        placeholder: "describe the reason for the draft rejection here...",
        buttonText: "Reject draft",
      };
    }
    if (location.pathname.endsWith("post-reject")) {
      return {
        title: "Reason for rejecting post",
        description: "Give a brief for the rejection of the post",
        placeholder: "describe the reason for the post rejection here...",
        buttonText: "Reject post",
      };
    }
    if (location.pathname.endsWith("disqualify")) {
      return {
        title: "Reason for disqualifying post",
        description: "Give a brief for the disqualification of the post",
        placeholder:
          "describe the reason for the post disqualification here...",
        buttonText: "Disqualify post",
      };
    }
    if (location.pathname.endsWith("flag")) {
      return {
        title: "Reason for flagging post",
        description: "Give a brief for the flagging of the post",
        placeholder: "describe the reason for the post flagging here...",
        buttonText: "Flag post",
      };
    }
  }

  return (
    <DialogContent className="flex flex-col items-center gap-5 border-border rounded-xl w-96 md:w-full font-cabinetgrotesk text-black text-center">
      <DialogHeader className="w-full">
        <div className="flex flex-col items-start">
          <p className="flex gap-1 font-semibold text-xl md:text-28 leading">
            {ModalType()?.title} <StarIcon />
          </p>
          <span className="text-secondary">{ModalType()?.description}</span>
        </div>
      </DialogHeader>
      <div className="border-border border-b w-full" />
      <div className="flex items-center gap-4 w-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex flex-col space-y-8 w-full"
          >
            <Controller
              control={form.control}
              name="message"
              render={({ field }) => (
                <Textarea
                  {...field}
                  label=""
                  placeholder={ModalType()?.placeholder || ""}
                />
              )}
            />

            <div className="flex justify-end w-full">
              <Button
                type="submit"
                variant="red"
                disabled={!form.formState.isValid}
                isLoading={
                  fetchingRejectdraft ||
                  fetchingRejectpost ||
                  fetchingDisqualifypost ||
                  fetchingFlagpost
                }
              >
                {ModalType()?.buttonText}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </DialogContent>
  );
};
